/**
 * EmptyState Component
 * 
 * Reusable empty state placeholder using MUI components.
 * Supports multiple variants, customizable icons, actions, and filter states.
 * 
 * @example
 * // Basic usage
 * <EmptyState title="No items" description="Add your first item" />
 * 
 * @example
 * // With filter state
 * <EmptyState 
 *   hasFilters 
 *   onClearFilters={() => setFilters({})} 
 * />
 * 
 * @example
 * // With action button
 * <EmptyState 
 *   title="No campaigns" 
 *   actionText="Create Campaign" 
 *   onAction={() => navigate('/create')} 
 * />
 */

import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Fade,
  CircularProgress,
} from '@mui/material';
import type { SvgIconProps, CircularProgressProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  Inbox as InboxIcon,
  Search as SearchIcon,
  ErrorOutline as ErrorIcon,
  FolderOpen as FolderIcon,
  FilterAlt as FilterIcon,
  Layers as LayersIcon,
  Add as AddIcon,
  FilterAltOff as ClearFilterIcon,
} from '@mui/icons-material';

// =============================================================================
// TYPES
// =============================================================================

export type EmptyStateVariant = 'default' | 'search' | 'error' | 'folder' | 'filter' | 'status';

// Generic icon-component props covering MUI (SvgIconProps) and Lucide ({ size, className })
type IconComponentProps = {
  size?: number | string;
  className?: string;
  sx?: SxProps<Theme>;
  fontSize?: SvgIconProps['fontSize'];
};

// Type for icon prop - accepts React elements, component types, or LucideIcon
type IconProp = React.ReactNode | React.ComponentType<IconComponentProps>;

const isForwardRefLike = (value: unknown): value is { $$typeof: symbol } =>
  typeof value === 'object' && value !== null && '$$typeof' in value;

export interface EmptyStateProps {
  /** Title text */
  title?: string;
  /** Description text */
  description?: string;
  /** Custom icon (MUI icon element, component type, or LucideIcon) */
  icon?: IconProp;
  /** Predefined variant (affects icon and default text) */
  variant?: EmptyStateVariant;
  /** Action button text */
  actionText?: string;
  /** Action button click handler */
  onAction?: () => void;
  /** Secondary action text */
  secondaryActionText?: string;
  /** Secondary action click handler */
  onSecondaryAction?: () => void;
  /** Whether filters are active */
  hasFilters?: boolean;
  /** Callback to clear filters */
  onClearFilters?: () => void;
  /** Whether user can create new items */
  canCreate?: boolean;
  /** Icon background color */
  iconBgColor?: string;
  /** Icon color */
  iconColor?: string;
  /** Primary button color */
  primaryColor?: string;
  /** Additional CSS class */
  className?: string;
  /** Additional styles */
  sx?: object;
}

// =============================================================================
// VARIANT CONFIG
// =============================================================================

interface VariantConfig {
  icon: React.ReactNode;
  title: string;
  description: string;
  iconBgColor: string;
  iconColor: string;
}

const getVariantConfig = (variant: EmptyStateVariant): VariantConfig => {
  const configs: Record<EmptyStateVariant, VariantConfig> = {
    default: {
      icon: <InboxIcon sx={{ fontSize: 32 }} />,
      title: 'No items yet',
      description: 'Get started by adding your first item.',
      iconBgColor: '#f3f4f6',
      iconColor: '#9ca3af',
    },
    search: {
      icon: <SearchIcon sx={{ fontSize: 32 }} />,
      title: 'No results found',
      description: 'Try adjusting your search or filters.',
      iconBgColor: '#eff6ff',
      iconColor: '#3b82f6',
    },
    error: {
      icon: <ErrorIcon sx={{ fontSize: 32 }} />,
      title: 'Something went wrong',
      description: 'Please try again or contact support.',
      iconBgColor: '#fef2f2',
      iconColor: '#c00035',
    },
    folder: {
      icon: <FolderIcon sx={{ fontSize: 32 }} />,
      title: 'This folder is empty',
      description: 'Add some items to this folder.',
      iconBgColor: '#fefce8',
      iconColor: '#eab308',
    },
    filter: {
      icon: <FilterIcon sx={{ fontSize: 32 }} />,
      title: 'No matches found',
      description: 'Try adjusting your filters or search criteria.',
      iconBgColor: '#faf5ff',
      iconColor: '#a855f7',
    },
    status: {
      icon: <LayersIcon sx={{ fontSize: 32 }} />,
      title: 'No items grouped by status',
      description: 'Items will appear here once they have a status assigned.',
      iconBgColor: '#f0fdf4',
      iconColor: '#22c55e',
    },
  };
  return configs[variant];
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon,
  variant = 'default',
  actionText,
  onAction,
  secondaryActionText,
  onSecondaryAction,
  hasFilters,
  onClearFilters,
  canCreate = true,
  iconBgColor,
  iconColor,
  primaryColor = '#2563eb',
  className = '',
  sx = {},
}) => {
  // Use filter variant if hasFilters is true
  const effectiveVariant = hasFilters ? 'filter' : variant;
  const config = getVariantConfig(effectiveVariant);

  // Handle icon - can be React element or component type
  const renderIcon = () => {
    if (!icon) return config.icon;
    // If icon is a valid element, use it directly
    if (React.isValidElement(icon)) return icon;
    // If icon is a component type (function or forwardRef), render it
    // forwardRef components have typeof === 'object' with $$typeof and render properties
    if (typeof icon === 'function' || isForwardRefLike(icon)) {
      const IconComponent = icon as React.ComponentType<IconComponentProps>;
      return <IconComponent size={32} className="text-current" />;
    }
    return icon;
  };

  const displayIcon = renderIcon();
  const displayTitle = title || config.title;
  const displayDescription = description || config.description;
  const bgColor = iconBgColor || config.iconBgColor;
  const iColor = iconColor || config.iconColor;

  return (
    <Fade in timeout={300}>
      <Paper
        elevation={0}
        className={className}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          py: 6,
          px: 3,
          borderRadius: 3,
          border: '1px solid #f3f4f6',
          backgroundColor: 'white',
          ...sx,
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: bgColor,
            color: iColor,
            mb: 2,
          }}
        >
          {displayIcon}
        </Box>

        {/* Title */}
        <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom>
          {displayTitle}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: 360 }}
        >
          {displayDescription}
        </Typography>

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Clear filters button */}
          {hasFilters && onClearFilters && (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<ClearFilterIcon />}
              onClick={onClearFilters}
              sx={{ textTransform: 'none' }}
            >
              Clear Filters
            </Button>
          )}

          {/* Secondary action */}
          {secondaryActionText && onSecondaryAction && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={onSecondaryAction}
              sx={{ textTransform: 'none' }}
            >
              {secondaryActionText}
            </Button>
          )}

          {/* Primary action */}
          {actionText && onAction && canCreate && !hasFilters && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAction}
              sx={{
                textTransform: 'none',
                backgroundColor: primaryColor,
                '&:hover': { backgroundColor: primaryColor, filter: 'brightness(0.9)' },
              }}
            >
              {actionText}
            </Button>
          )}
        </Box>
      </Paper>
    </Fade>
  );
};

// =============================================================================
// STATUS VIEW EMPTY
// =============================================================================

export interface StatusViewEmptyProps {
  /** Title text */
  title?: string;
  /** Description text */
  description?: string;
  /** Custom icon */
  icon?: IconProp;
  /** Additional className */
  className?: string;
}

export const StatusViewEmpty: React.FC<StatusViewEmptyProps> = ({
  title = 'No items grouped by status',
  description = 'Items will appear here once they have a status assigned.',
  icon,
  className = '',
}) => {
  // Handle icon - can be React element or component type
  const renderIcon = () => {
    if (!icon) return <LayersIcon sx={{ fontSize: 48 }} />;
    if (React.isValidElement(icon)) return icon;
    // Handle function components and forwardRef components (forwardRef has typeof === 'object' with $$typeof)
    if (typeof icon === 'function' || isForwardRefLike(icon)) {
      const IconComponent = icon as React.ComponentType<IconComponentProps>;
      return <IconComponent size={48} className="text-current" />;
    }
    return icon;
  };

  return (
    <Fade in timeout={300}>
      <Paper
        elevation={0}
        className={className}
        sx={{
          textAlign: 'center',
          py: 6,
          borderRadius: 3,
          border: '1px solid #f3f4f6',
          backgroundColor: 'white',
        }}
      >
        <Box sx={{ color: '#d1d5db', mb: 2 }}>
          {renderIcon()}
        </Box>
        <Typography color="text.secondary">{title}</Typography>
        {description && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
            {description}
          </Typography>
        )}
      </Paper>
    </Fade>
  );
};

// =============================================================================
// LOADING STATE
// =============================================================================

export interface LoadingStateProps {
  /** Loading text */
  text?: string;
  /** Additional className */
  className?: string;
  /** Spinner color */
  color?: CircularProgressProps['color'];
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  text = 'Loading...',
  className = '',
  color = 'primary',
}) => {
  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 10,
        gap: 2,
      }}
    >
      <CircularProgress color={color} size={40} />
      <Typography color="text.secondary">{text}</Typography>
    </Box>
  );
};

export default EmptyState;
